import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface DemoModeContextType {
  /** True only when the server reports demo mode as effectively ON (UAT only). */
  demoMode: boolean
  /** True until the first config fetch finishes. */
  loading: boolean
  /** Re-fetch the flag from the server (e.g. after an admin toggles it). */
  refreshDemoMode: () => Promise<void>
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined)

/**
 * Loads the public config flag `demoMode` from the backend (GET /api/config/public).
 * The registration pages run before login, so this endpoint is unauthenticated.
 * Any failure resolves to demoMode = false (fail closed).
 */
export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [demoMode, setDemoMode] = useState(false)
  const [loading, setLoading] = useState(true)

  const refreshDemoMode = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/public`, { cache: 'no-store' })
      if (!response.ok) {
        setDemoMode(false)
        return
      }
      const body = await response.json()
      setDemoMode(body?.data?.demoMode === true)
    } catch {
      setDemoMode(false)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshDemoMode()
  }, [refreshDemoMode])

  return (
    <DemoModeContext.Provider value={{ demoMode, loading, refreshDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  )
}

export const useDemoMode = () => {
  const context = useContext(DemoModeContext)
  if (context === undefined) {
    throw new Error('useDemoMode must be used within DemoModeProvider')
  }
  return context
}
